import { SingleSelectField, TextField } from "@dgs/core";
import { useField } from "formik";
import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CountryMultiSelectField, CountrySelectField } from "../../../shared/ui/CountrySelectField";
import { TagMultiSelectField } from "../../../shared/ui/TagSelectField";
import { UserTenantMultiSelect, UserTenantSingleSelect } from "../../../shared/ui/UserTenantSelect";
import { CompanyMultiSelectField, CompanySelectField } from "../companies/CompanySelectField";
import { BetweenNumberValueField } from "../contactFilter/BetweenValueField";
import { ContactStatusSingleSelect } from "../contactFilter/ContactStatusSingleSelect";
import { DistributionListMultiSelectField, DistributionListSelectField } from "../distributionLists";
import { ContactTypeMultiSelect } from "../shared/select/ContactTypeMultiSelect";
import { ContactTypeSingleSelect } from "../shared/select/ContactTypeSingleSelect";
import { EventMultiSelect, EventSingleSelect } from "../shared/select/EventSelect";
import {
	ContactAttributeResourceRelation,
	ContactFilterOperator,
	ContactRelationColumn,
	ContactRelationEntityTypeToColumns,
	contactRelationEntityTypeToColumns,
} from "../shared/types";
import { ContactFilterOperatorAndValue } from "./ContactFilterOperatorAndValue";
import { IFilterEntryDescription } from "./types";

interface Props {
	name: string;
	attribute: ContactAttributeResourceRelation;
}

type RelationFilterEntryDescription = {
	[Relation in keyof ContactRelationEntityTypeToColumns]: {
		[Column in ContactRelationEntityTypeToColumns[Relation][number]]: IFilterEntryDescription;
	};
};

const addressDescription: RelationFilterEntryDescription["addresses"] = {
	street: {
		operators: ["IS_EMPTY", "IS_NOT_EMPTY", "CONTAINS", "NOT_CONTAINS", "EQUAL", "NOT_EQUAL"],
		render: (props) => <TextField {...props} />,
	},
	zip: {
		operators: ["IS_EMPTY", "IS_NOT_EMPTY", "CONTAINS", "NOT_CONTAINS", "EQUAL", "NOT_EQUAL", "BETWEEN"],
		render: (props, operator) => {
			switch (operator) {
				case "BETWEEN":
					return <BetweenNumberValueField name={props.name} />;
				default:
					return <TextField {...props} />;
			}
		},
	},
	houseNumber: {
		operators: ["IS_EMPTY", "IS_NOT_EMPTY", "CONTAINS", "NOT_CONTAINS", "EQUAL", "NOT_EQUAL"],
		render: (props) => <TextField {...props} />,
	},
	city: {
		operators: ["IS_EMPTY", "IS_NOT_EMPTY", "CONTAINS", "NOT_CONTAINS", "EQUAL", "NOT_EQUAL"],
		render: (props) => <TextField {...props} />,
	},
	countryId: {
		operators: ["IS_EMPTY", "IS_NOT_EMPTY", "EQUAL", "NOT_EQUAL", "WHERE_IN"],
		render: (props, operator) => {
			switch (operator) {
				case "WHERE_IN":
					return <CountryMultiSelectField {...props} />;
				default:
					return <CountrySelectField {...props} />;
			}
		},
	},
	additionalInfo: {
		operators: ["IS_EMPTY", "IS_NOT_EMPTY", "CONTAINS", "NOT_CONTAINS", "EQUAL", "NOT_EQUAL"],
		render: (props) => <TextField {...props} />,
	},
};

const emailDescription: RelationFilterEntryDescription["emails"] = {
	email: {
		operators: ["IS_EMPTY", "IS_NOT_EMPTY", "CONTAINS", "NOT_CONTAINS", "EQUAL", "NOT_EQUAL"],
		render: (props) => <TextField {...props} />,
	},
};

const phoneDescription: RelationFilterEntryDescription["phones"] = {
	phoneNumber: {
		operators: ["IS_EMPTY", "IS_NOT_EMPTY", "CONTAINS", "NOT_CONTAINS", "EQUAL", "NOT_EQUAL"],
		render: (props) => <TextField type="tel" {...props} />,
	},
};
const companyDescription: RelationFilterEntryDescription["companies"] = {
	id: {
		operators: ["IS_EMPTY", "IS_NOT_EMPTY", "EQUAL", "NOT_EQUAL", "WHERE_IN"],
		render: (props, operator) => {
			switch (operator) {
				case "WHERE_IN":
					return <CompanyMultiSelectField {...props} />;
				default:
					return <CompanySelectField {...props} />;
			}
		},
	},
};

const descriptions: RelationFilterEntryDescription = {
	distributionLists: {
		id: {
			operators: ["IS_EMPTY", "IS_NOT_EMPTY", "EQUAL", "NOT_EQUAL", "WHERE_IN"],
			render: (props, operator) => {
				switch (operator) {
					case "WHERE_IN":
						return <DistributionListMultiSelectField {...props} />;
					default:
						return <DistributionListSelectField {...props} />;
				}
			},
		},
	},
	invitationLists: {
		id: {
			operators: ["IS_EMPTY", "IS_NOT_EMPTY", "EQUAL", "NOT_EQUAL", "WHERE_IN"],
			render: (props, operator) => {
				switch (operator) {
					case "WHERE_IN":
						return <EventMultiSelect {...props} />;
					default:
						return <EventSingleSelect {...props} />;
				}
			},
		},
	},
	tenants: {
		id: {
			operators: ["IS_EMPTY", "IS_NOT_EMPTY", "EQUAL", "NOT_EQUAL", "WHERE_IN"],
			render: (props, operator) => {
				switch (operator) {
					case "WHERE_IN":
						return <UserTenantMultiSelect {...props} />;
					default:
						return <UserTenantSingleSelect {...props} />;
				}
			},
		},
	},
	tags: {
		id: {
			operators: ["IS_EMPTY", "IS_NOT_EMPTY", "EQUAL", "NOT_EQUAL", "WHERE_IN"],
			render: (props) => <TagMultiSelectField {...props} />,
		},
	},
	contactType: {
		id: {
			operators: ["EQUAL", "NOT_EQUAL", "WHERE_IN"],
			render: (props, operator) => {
				switch (operator) {
					case "WHERE_IN":
						return <ContactTypeMultiSelect {...props} />;
					default:
						return <ContactTypeSingleSelect {...props} />;
				}
			},
		},
	},
	companies: companyDescription,
	primaryCompany: companyDescription,
	nonPrimaryCompanies: companyDescription,
	addresses: addressDescription,
	primaryAddress: addressDescription,
	nonPrimaryAddresses: addressDescription,
	emails: emailDescription,
	primaryEmail: emailDescription,
	nonPrimaryEmails: emailDescription,
	phones: phoneDescription,
	primaryPhone: phoneDescription,
	nonPrimaryPhones: phoneDescription,
	applications: {
		status: {
			operators: ["IS_EMPTY", "IS_NOT_EMPTY", "EQUAL", "NOT_EQUAL"],
			render: (props) => <ContactStatusSingleSelect {...props} />,
		},
	},
};

export const ContactFilterRelationEntry: FC<Props> = ({ name, attribute }) => {
	const [, , { setValue }] = useField<unknown>(`${name}.value`);
	const [{ value: selectedRelationColumn }] = useField<ContactRelationColumn>(`${name}.relationColumn`);
	const [, , { setValue: setOperator }] = useField<ContactFilterOperator | null>(`${name}.operator`);
	const { t } = useTranslation();
	const resetFilter = useCallback(async () => {
		await setOperator(null, false);
		await setValue(null, false);
	}, [setOperator, setValue]);

	const relationColumnDescriptions = descriptions[attribute.entityType];
	const availableRelationColumns = useMemo(
		() => contactRelationEntityTypeToColumns[attribute.entityType].slice(0),
		[attribute.entityType],
	);

	return (
		<>
			{availableRelationColumns.length > 1 && (
				<SingleSelectField
					label={t("Column")}
					name={`${name}.relationColumn`}
					options={availableRelationColumns}
					getLabel={(column) => t(column)}
					onChange={resetFilter}
				/>
			)}
			{selectedRelationColumn && (
				<ContactFilterOperatorAndValue
					name={name}
					type={selectedRelationColumn}
					description={(relationColumnDescriptions as any)[selectedRelationColumn]}
				/>
			)}
		</>
	);
};
